/**
 * `app-zone-slider` — a value slider on a track split into coloured zones,
 * with a value pill (plus a secondary line such as margin) and a badge naming
 * the zone the value sits in. Pure HTML/CSS: the thumb is drawn by this
 * component; an invisible native `<input type="range">` on top provides drag,
 * keyboard and screen-reader behaviour.
 *
 * Same contract as the other shared input controls: `[data]` in,
 * `(valueChange)` out on release. Hosts: the Design Builder (canvas, preview
 * and published view) and `ChartRendererService` as `zone-slider`.
 */

import { CommonModule } from '@angular/common';
import { isMandatoryControl } from '../../utils/mandatory-control.utils';
import {
  ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges,
} from '@angular/core';

import {
  ZONE_SLIDER_DEFAULTS, type ZoneSliderData, type ZoneSliderModel, finiteOrNull, resolveZoneSliderModel,
} from './zone-slider.utils';

let nextId = 0;

@Component({
  selector: 'app-zone-slider',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
  templateUrl: './zone-slider.component.html',
  styleUrl: './zone-slider.component.scss',
})
export class ZoneSliderComponent implements OnChanges {
  @Input() data: ZoneSliderData | null = null;
  /** Emitted when the user releases the thumb (or commits a keyboard change). */
  @Output() valueChange = new EventEmitter<number>();

  readonly inputId = `zone-slider-${++nextId}`;
  model: ZoneSliderModel = resolveZoneSliderModel({});

  /** The value being dragged. Reset only when the INCOMING value changes, so an
   *  unrelated re-render (any other setting edited) does not snap the thumb back. */
  private current: number | null = null;
  private lastIncoming: number | null | undefined = undefined;

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['data']) return;
    const incoming = finiteOrNull(this.data?.value);
    if (incoming !== this.lastIncoming) {
      this.lastIncoming = incoming;
      this.current = incoming;
    }
    this.rebuild();
  }

  get config(): ZoneSliderData {
    return this.data ?? {};
  }

  get showValue(): boolean { return this.config.showValue ?? ZONE_SLIDER_DEFAULTS.showValue; }
  get showBadge(): boolean { return this.config.showBadge ?? ZONE_SLIDER_DEFAULTS.showBadge; }
  get showRangeLabels(): boolean { return this.config.showRangeLabels ?? ZONE_SLIDER_DEFAULTS.showRangeLabels; }
  get showBoundaryValues(): boolean { return this.config.showBoundaryValues === true; }
  get disabled(): boolean { return this.config.disabled === true; }
  /** Config `mandatory` → `*` after the label. Presentation only. */
  get isMandatory(): boolean { return isMandatoryControl(this.config); }

  /** Styling knobs as CSS custom properties, so the stylesheet stays declarative. */
  get hostVars(): Record<string, string> {
    const c = this.config;
    const d = ZONE_SLIDER_DEFAULTS;
    const fontSize = finiteOrNull(c.fontSize) ?? d.fontSize;
    const trackHeight = finiteOrNull(c.trackHeight) ?? d.trackHeight;
    return {
      '--zs-font-size': `${fontSize}px`,
      '--zs-track-height': `${trackHeight}px`,
      '--zs-pill-bg': str(c.pillBackground) || d.pillBackground,
      '--zs-pill-color': str(c.pillColor) || d.pillColor,
      '--zs-sub-color': str(c.subLabelColor) || d.subLabelColor,
      '--zs-thumb-color': str(c.thumbColor) || d.thumbColor,
      '--zs-thumb-border': str(c.thumbBorderColor) || d.thumbBorderColor,
      '--zs-badge-color': this.model.badgeColor,
      ...(str(c.fontFamily) ? { '--zs-font': c.fontFamily as string } : {}),
    };
  }

  onInput(event: Event): void {
    const next = Number((event.target as HTMLInputElement).value);
    if (!Number.isFinite(next)) return;
    this.current = next;
    this.rebuild();
  }

  onCommit(): void {
    if (this.current === null) return;
    this.valueChange.emit(this.model.value);
  }

  trackZone(index: number): number {
    return index;
  }

  private rebuild(): void {
    this.model = resolveZoneSliderModel(this.config, this.current);
  }
}

function str(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}
